import React, { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  ArrowDropDown,
  CenterFocusStrongOutlined,
  Check,
  CheckCircleOutline,
  CloudOffOutlined,
  ContentCopy,
  DescriptionOutlined,
  ErrorOutline,
  FileDownloadOutlined,
  MarginOutlined,
  MoreVert,
  OpenInNew,
  PictureAsPdfOutlined,
  SaveOutlined,
  SlideshowOutlined,
  ViewAgendaOutlined,
  WidthNormal,
  WidthWide,
} from '@mui/icons-material';
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';
import documentService from '../services/documentService';
import { useActiveEditor } from '../context/ActiveEditorContext';
import { useAppActions } from '../context/AppContext';
import { quoteForInky, useEditorChrome, useWorkspaceLayout } from '../context/WorkspaceLayoutContext';
import { extractTrustComments } from '../utils/trustCitationUtils';
import DocumentSourcesChip from './documents/DocumentSourcesChip';
import useAutosave from './editor/useAutosave';
import { PageSetupProvider } from './editor/pageSetupContext';
import { buildFieldContext, normalizePageSetup, serializePageSetup } from '../utils/pageSetup';
import {
  DocumentConflictError,
  notifyOpenerDocumentUpdated,
  packDocumentSnapshot,
  saveDocumentContent,
  unpackDocumentSnapshot,
} from './editor/documentPersistence';
import { loadTemplateField } from './editor/regenerateField';
import { buildAskAboutSectionPrompt, buildRegeneratePrompt, buildSourceFixPrompt, REGENERATE_GUIDANCE_CHIPS } from './editor/sectionHandoff';
import EntityBreadcrumbs from './layout/EntityBreadcrumbs';
import DeckGenerationNotice from './documents/DeckGenerationNotice';
import { EditorJobsButton, EditorNavButton, InkyReopenButton } from './layout/EditorChromeButtons';
import { formatShortcut } from '../hooks/useWorkspaceShortcuts';
import { PAPER_SIZE_IDS, PAPER_SIZES, readPaperSize, subscribePaperSize, writePaperSize } from '../utils/paperSize';
import {
  documentEditorPath,
  forgetRecentDocument,
  openInNewTab,
  projectPath,
  recordRecentDocument,
  registerUnsavedWorkFlusher,
} from '../utils/navigation';

/** Sticky document header height on one row (px); the measured height is used when it wraps. */
const HEADER_HEIGHT = 64;

/**
 * Whether editor HTML has no visible content (text, images, diagrams or tables).
 * @param {string} html - Editor HTML.
 * @returns {boolean} True when empty.
 */
const isEmptyHtml = (html) => {
  if (!html) return true;
  if (/<(img|table|hr)\b|data-mermaid-block|data-tpl-field|data-field-id/i.test(html)) return false;
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;| |​/g, '').trim() === '';
};

/**
 * Human message for a document that cannot be opened yet.
 * @param {object} data - Document row (with ai_requests).
 * @returns {string} Message.
 */
const emptyDocumentMessage = (data) => {
  const aiStatus = data?.ai_requests?.status;
  if (aiStatus === 'pending' || aiStatus === 'processing') {
    return 'This document is still being generated. Please wait a few moments and try again.';
  }
  if (aiStatus === 'failed') return 'Document generation failed. Please try regenerating the document.';
  return 'This document appears to have no content. It may need to be regenerated.';
};

/**
 * Whether a document is a slide deck (PowerPoint template output), from the row as loaded: the
 * stored `<!--ppt_slides:...-->` embed or the deck trust_metadata the worker writes. Never from
 * the live editor HTML, because the editor drops HTML comments on the first edit.
 * @param {object|null} data - Document row.
 * @returns {boolean} True for decks.
 */
const isDeckDocument = (data) => {
  if (/<!--ppt_slides:/.test(data?.content || '')) return true;
  let trust = data?.trust_metadata ?? data?.trustMetadata ?? null;
  if (typeof trust === 'string') {
    try {
      trust = JSON.parse(trust);
    } catch {
      trust = null;
    }
  }
  return trust?.kind === 'presentation';
};

/**
 * File-name-safe base name.
 * @param {string} title - Document title.
 * @returns {string} Base name.
 */
const fileBaseName = (title) => (title || 'Document').replace(/\.(docx|pdf|html|pptx)$/i, '').replace(/[\\/:*?"<>|]+/g, '-').trim() || 'Document';

/**
 * The single save status, a plain label next to the title (same as the template editor). The
 * tooltip (also on keyboard focus) gives the last saved time or the exact reason a save failed.
 * @param {{status: string, error?: string|null, lastSavedAt?: number|null, halted?: boolean}} props - Props.
 * @returns {JSX.Element|null} Label.
 */
export const SaveStatusChip = ({ status, error = null, lastSavedAt = null, halted = false }) => {
  const savedAt = lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
  const map = {
    saving: { label: 'Saving…', icon: <CircularProgress size={11} thickness={6} color="inherit" />, color: 'text.secondary', detail: null },
    saved: { label: 'Saved', icon: <CheckCircleOutline sx={{ fontSize: 14 }} />, color: 'var(--q-success-text)', detail: savedAt ? `Last saved at ${savedAt}` : 'All changes saved' },
    dirty: { label: 'Unsaved changes', icon: null, color: 'text.secondary', detail: 'Saving shortly' },
    error: {
      label: halted ? 'Not saved' : 'Save failed, retrying',
      icon: <ErrorOutline sx={{ fontSize: 14 }} />,
      color: 'var(--q-danger-text)',
      detail: `${error || 'The last save failed.'} ${halted ? `Autosave is paused: press ${formatShortcut('Mod+S')} to try again.` : 'Retrying automatically.'}`,
    },
    offline: { label: 'Offline, will retry', icon: <CloudOffOutlined sx={{ fontSize: 14 }} />, color: 'var(--q-warning-text)', detail: 'You are offline. Changes save when you reconnect.' },
    conflict: { label: 'Changed elsewhere', icon: <ErrorOutline sx={{ fontSize: 14 }} />, color: 'var(--q-danger-text)', detail: error || 'This document was changed elsewhere.' },
  };
  const entry = map[status];
  if (!entry) return null;
  const label = (
    <Box
      component="span"
      role="status"
      aria-live="polite"
      tabIndex={entry.detail ? 0 : undefined}
      data-testid="document-save-status"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flex: 'none', fontSize: 12.5, whiteSpace: 'nowrap', color: entry.color, borderRadius: '4px' }}
    >
      {entry.icon}
      {entry.label}
    </Box>
  );
  return entry.detail ? <Tooltip title={entry.detail} describeChild>{label}</Tooltip> : label;
};

/**
 * In-app document workspace (`/documents/:documentId/edit`, CONTRACTS-W2 §A). Renders inside
 * the app shell beside Inky. The editor is uncontrolled; content is read from the editor ref at
 * save time. Saves are debounced autosaves with an optimistic-concurrency check (one DB write),
 * Mod-S, an unload guard and an in-app navigation guard that saves before leaving. Lists in this
 * window update through AppContext.patchGeneratedDocumentLocal; an opener tab (when the document
 * was opened with "Open in new tab") is notified with DOCUMENT_UPDATED.
 */
const StandaloneEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const documentId = params.documentId || searchParams.get('documentId');
  const queryProjectId = params.projectId || searchParams.get('projectId') || null;
  const appActions = useAppActions();
  const {
    editorPageMode,
    setEditorPageMode,
    focusMode,
    setFocusMode,
    openInky,
  } = useWorkspaceLayout();
  // Editor chrome: the document header replaces the shell's top bar and sidebar here.
  useEditorChrome();

  const [documentData, setDocumentData] = useState(null);
  const [initialContent, setInitialContent] = useState('');
  const [loadVersion, setLoadVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [liveHtml, setLiveHtml] = useState('');
  const [toast, setToast] = useState(null);
  const [editorReady, setEditorReady] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  // Page setup (orientation, margins, header and footer): the row's page_setup (null = defaults).
  const [pageSetup, setPageSetup] = useState(null);
  const [pageSetupOpen, setPageSetupOpen] = useState(false);
  // Pages view paper size: a per-browser preference shared with the exporters.
  const [paperSize, setPaperSize] = useState(readPaperSize);
  useEffect(() => subscribePaperSize(setPaperSize), []);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(HEADER_HEIGHT);
  const headerRef = useRef(null);
  const editorRef = useRef(null);
  const updatedAtRef = useRef(null);
  /** Editor HTML right after the document loaded (null until then): an unchanged deck exports its stored slides. */
  const loadedEditorHtmlRef = useRef(null);
  /** Latest page setup, read by autosave at save time (page-setup-only edits make it dirty too). */
  const pageSetupRef = useRef(null);
  pageSetupRef.current = pageSetup;
  /** serializePageSetup of the page_setup the row holds (loaded or last saved): unchanged = not written. */
  const savedPageSetupKeyRef = useRef(serializePageSetup(null));
  const forceNextSaveRef = useRef(false);
  /** Set when a forced ("Keep mine") write actually reached the server. */
  const forcedWriteDoneRef = useRef(false);
  const { registerEditor, unregisterEditor } = useActiveEditor();

  const deferredHtml = useDeferredValue(liveHtml);
  const sourceItems = useMemo(() => extractTrustComments(deferredHtml), [deferredHtml]);
  const title = documentData?.name || 'Untitled Document';
  const projectId = documentData?.project_id || queryProjectId;

  useEffect(() => {
    if (!editorReady) return undefined;
    registerEditor(editorRef);
    return () => unregisterEditor(editorRef);
  }, [editorReady, registerEditor, unregisterEditor]);

  useEffect(() => {
    if (documentData?.name) document.title = `${documentData.name} - Quill AI`;
  }, [documentData?.name]);

  // Autosave works on one string per version: packDocumentSnapshot(html, pageSetup). Content and
  // page setup are written by the same conflict-checked UPDATE; page_setup only when it changed
  // since load/last save (or on a forced "Keep mine" write, which keeps this tab's whole version).
  const save = useCallback(async (snapshot) => {
    const { html: content, pageSetup: snapshotPageSetup, pageSetupKey } = unpackDocumentSnapshot(snapshot);
    const force = forceNextSaveRef.current;
    const writePageSetup = pageSetupKey !== null && (force || pageSetupKey !== savedPageSetupKeyRef.current);
    const { updatedAt } = await saveDocumentContent({
      documentId,
      content,
      ...(writePageSetup ? { pageSetup: snapshotPageSetup } : {}),
      expectedUpdatedAt: updatedAtRef.current,
      force,
    });
    updatedAtRef.current = updatedAt;
    // Also when the column is missing (migration not applied): retrying every save cannot help.
    if (writePageSetup) savedPageSetupKeyRef.current = pageSetupKey;
    if (forceNextSaveRef.current) forcedWriteDoneRef.current = true;
    forceNextSaveRef.current = false;
    appActions.patchGeneratedDocumentLocal(documentId, {
      content,
      updatedAt,
      ...(writePageSetup ? { pageSetup: snapshotPageSetup } : {}),
    });
    // The opener's exports read page setup from its own state: send it whenever it was written.
    notifyOpenerDocumentUpdated({
      documentId,
      updatedAt,
      content,
      title,
      ...(writePageSetup ? { pageSetup: snapshotPageSetup } : {}),
    });
  }, [documentId, title, appActions]);

  const getContent = useCallback(() => {
    const html = editorRef.current?.getHTML?.();
    if (typeof html !== 'string' || isEmptyHtml(html)) return null;
    return packDocumentSnapshot(html, pageSetupRef.current);
  }, []);

  const autosave = useAutosave({
    getContent,
    save,
    delay: 1500,
    enabled: Boolean(documentId) && !loading && !loadError,
    isConflictError: (error) => error instanceof DocumentConflictError,
  });
  const { setBaseline, markDirty, saveNow, clearConflict, isDirty } = autosave;

  /** Page setup dialog Save: applies to this document only; autosave writes it. */
  const handlePageSetupChange = useCallback((next) => {
    pageSetupRef.current = next;
    setPageSetup(next);
    markDirty();
  }, [markDirty]);

  // The project's and customer's names for the live fields. AppContext's projects may not be
  // loaded yet (document opened by direct link) and are not reactive here, so read the project
  // row when it is not there.
  // Runs per project only (the actions are read through a ref, so a changing actions object
  // cannot re-run it).
  const [projectLabels, setProjectLabels] = useState(null);
  const appActionsRef = useRef(appActions);
  appActionsRef.current = appActions;
  useEffect(() => {
    setProjectLabels(null);
    if (!projectId) return undefined;
    const known = appActionsRef.current.getProjectById?.(projectId);
    if (known) {
      setProjectLabels({ project: known.name || '', customer: known.customer?.name || '' });
      return undefined;
    }
    if (typeof documentService.getProjectLabels !== 'function') return undefined;
    let cancelled = false;
    documentService.getProjectLabels(projectId).then((labels) => {
      if (!cancelled && labels) setProjectLabels(labels);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Live fields for headers and footers: title, the project's customer and name, created date.
  const fieldContext = useMemo(() => buildFieldContext({
    title,
    customer: projectLabels?.customer || '',
    project: projectLabels?.project || '',
    createdAt: documentData?.created_at || null,
  }), [title, projectLabels, documentData?.created_at]);

  /**
   * Fetch the document and load it into the editor.
   * @returns {Promise<void>}
   */
  const loadDocument = useCallback(async () => {
    if (!documentId) {
      setLoadError('No document ID provided');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const data = await documentService.getDocumentById(documentId);
      setDocumentData(data);
      recordRecentDocument({ id: data.id, name: data.name, projectId: data.project_id });
      if (!data.content || data.content.trim() === '') {
        setLoadError(emptyDocumentMessage(data));
        return;
      }
      updatedAtRef.current = data.updated_at || null;
      loadedEditorHtmlRef.current = null;
      // page_setup is undefined while the column does not exist yet: same as null (defaults).
      const loadedPageSetup = data.page_setup == null ? null : normalizePageSetup(data.page_setup);
      pageSetupRef.current = loadedPageSetup;
      savedPageSetupKeyRef.current = serializePageSetup(loadedPageSetup);
      setPageSetup(loadedPageSetup);
      setInitialContent(data.content);
      setLiveHtml(data.content);
      setLoadVersion((v) => v + 1);
    } catch (error) {
      console.error('Error fetching document:', error);
      if (error?.code === 'PGRST116') forgetRecentDocument(documentId);
      setLoadError(`Failed to load document: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  // Baseline the autosave with the editor's normalized HTML (and the loaded page setup) once
  // content is loaded.
  useEffect(() => {
    if (!editorReady || !loadVersion) return;
    const html = editorRef.current?.getHTML?.();
    if (typeof html === 'string') {
      loadedEditorHtmlRef.current = html;
      setBaseline(packDocumentSnapshot(html, pageSetupRef.current));
    }
  }, [editorReady, loadVersion, setBaseline]);

  const handleChange = useCallback((html) => setLiveHtml(html), []);

  const handleManualSave = useCallback(async () => {
    const html = editorRef.current?.getHTML?.();
    if (!html || isEmptyHtml(html)) {
      setToast({ severity: 'warning', message: 'Empty documents are not saved.' });
      return;
    }
    const ok = await saveNow();
    if (ok) setToast({ severity: 'success', message: 'Saved' });
  }, [saveNow]);

  /** Reload the server version, discarding local edits (conflict resolution). */
  const handleReloadFromServer = useCallback(async () => {
    clearConflict();
    await loadDocument();
  }, [clearConflict, loadDocument]);

  /**
   * Overwrite the server version with the local one (conflict resolution). Always performs the
   * forced write (even when the content equals the last saved version), never leaves the force
   * flag armed, and only confirms when a write actually happened.
   */
  const handleOverwrite = useCallback(async () => {
    forceNextSaveRef.current = true;
    forcedWriteDoneRef.current = false;
    clearConflict();
    try {
      const ok = await saveNow({ force: true });
      if (ok && forcedWriteDoneRef.current) setToast({ severity: 'success', message: 'Your version was saved.' });
    } finally {
      forceNextSaveRef.current = false;
      forcedWriteDoneRef.current = false;
    }
  }, [clearConflict, saveNow]);

  /**
   * Suggestions waiting for review (Inky's edits, Regenerate section) live only in the editor's
   * memory and are not part of the saved document, so leaving discards them silently.
   * @returns {{label?: string}|null} The pending review, or null.
   */
  const getPendingReview = useCallback(() => {
    try {
      const pending = editorRef.current?.getPendingReview?.();
      return pending && pending.changes?.length ? pending : null;
    } catch {
      return null;
    }
  }, []);

  // In-app navigation away (links, Inky's app.navigate, back): confirm discarding suggestions
  // that are still waiting for review, then save pending edits first.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname && (isDirty() || Boolean(getPendingReview()))
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let cancelled = false;
    (async () => {
      const pending = getPendingReview();
      if (pending) {
        const whose = /inky/i.test(pending.label || '') ? "Inky's suggestions are" : 'Suggested changes are';
        if (!window.confirm(`${whose} still waiting for your review. Leave and discard them?`)) {
          blocker.reset();
          return;
        }
      }
      const saved = isDirty() ? await saveNow() : true;
      if (cancelled) return;
      if (saved || window.confirm('Some changes could not be saved. Leave anyway?')) blocker.proceed();
      else blocker.reset();
    })();
    return () => {
      cancelled = true;
    };
  }, [blocker, saveNow, isDirty, getPendingReview]);

  // Refresh or tab close with suggestions still waiting for review: ask the browser to confirm
  // (unsaved edits are covered by useAutosave's own guard).
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!getPendingReview()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [getPendingReview]);

  // Actions that unmount the page without a router navigation (logging out flips the shell
  // off) save pending edits through this hook first; the router blocker never sees them.
  useEffect(
    () => registerUnsavedWorkFlusher(async () => (isDirty() ? saveNow() : true)),
    [isDirty, saveNow]
  );

  // When this document runs in a tab opened from another Quill tab, tell the opener it closed
  // so the opener's list refreshes right away.
  useEffect(() => {
    const notifyClose = () => {
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({ type: 'DOCUMENT_EDITOR_CLOSE', data: { documentId } }, window.location.origin);
        }
      } catch {
        /* opener gone or cross-origin */
      }
    };
    window.addEventListener('pagehide', notifyClose);
    return () => window.removeEventListener('pagehide', notifyClose);
  }, [documentId]);

  const handleBack = useCallback(() => {
    navigate(projectId ? projectPath(projectId, 'documents') : '/documents');
  }, [navigate, projectId]);

  /**
   * "Regenerate with Inky" / "Fill this section with Inky" on a generated section: opens Inky
   * with a request PREFILLED (not sent) that asks for a fresh version from the project's
   * requirements and meetings and ends with "Guidance: ", plus one-click guidance chips. The
   * section's template instruction is added when the template still has it. Inky's edit comes
   * back as a review (accept/reject).
   * The section (or inline field) is selected in the editor first, so Inky edits exactly it;
   * the request is worded around that selection and never shows internal ids.
   * @param {{fieldId: string|null, heading: string, status: string|null, text: string, inline?: boolean, element?: HTMLElement}} info - Section (GeneratedSectionChrome).
   * @returns {Promise<void>}
   */
  const handleRegenerateSection = useCallback(async (info) => {
    const bridge = editorRef.current;
    if (bridge?.isDiffMode) {
      setToast({ severity: 'info', message: 'Accept or reject the open changes first.' });
      return;
    }
    const selection = bridge?.selectGeneratedSection?.({ fieldId: info?.fieldId, element: info?.element }) || { ok: false };
    const inline = selection.ok ? selection.inline : Boolean(info?.inline);
    const currentValue = selection.ok ? selection.text : info?.text || '';
    const templateId = documentData?.template_id || null;
    const field = info?.fieldId && templateId ? await loadTemplateField(templateId, info.fieldId) : null;
    const needs = /\[Needs input:\s*([^\]]+)\]/i.exec(currentValue || info?.text || '');
    openInky({
      prompt: buildRegeneratePrompt({
        heading: info?.heading,
        status: info?.status,
        needsInput: needs ? needs[1] : null,
        instruction: field?.instruction || null,
        inline,
        currentValue: inline ? currentValue : '',
      }),
      send: false,
      chips: REGENERATE_GUIDANCE_CHIPS,
    });
  }, [documentData, openInky]);

  /** "Ask Inky about this": the section selected and quoted in the composer, never sent. */
  const handleAskAboutSection = useCallback((info) => {
    editorRef.current?.selectGeneratedSection?.({ fieldId: info?.fieldId, element: info?.element });
    openInky({ prompt: buildAskAboutSectionPrompt({ heading: info?.heading, text: info?.text }) });
  }, [openInky]);

  const generatedSectionActions = useMemo(
    () => ({ onRegenerate: handleRegenerateSection, onAsk: handleAskAboutSection }),
    [handleRegenerateSection, handleAskAboutSection]
  );

  /** Sources popover: jump to and select a section. */
  const handleSelectSource = useCallback((item) => {
    const bridge = editorRef.current;
    if (!bridge) return;
    if (!bridge.selectTrustSection?.(item.sectionIndex)) bridge.scrollToTrustSection?.(item.sectionIndex);
  }, []);

  /** Sources popover: "Fix with Inky" for a section that needs a source (sent at once). */
  const handleFixSource = useCallback((item) => {
    editorRef.current?.selectTrustSection?.(item.sectionIndex);
    openInky({ prompt: buildSourceFixPrompt(item), send: true });
  }, [openInky]);

  // The header wraps on narrow screens; the toolbar sticks right below its real height.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => {
      const next = Math.round(el.getBoundingClientRect().height) || HEADER_HEIGHT;
      setHeaderHeight((prev) => (prev === next ? prev : next));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The opener may ask this tab to save and go back.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'DOCUMENT_EDITOR_CLOSE_REQUEST') {
        saveNow().finally(handleBack);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleBack, saveNow]);

  // Mod-S anywhere on the page (the editor handles it while focused inside).
  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        handleManualSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleManualSave]);

  /** "Ask Inky" from the editor's inline AI menu: quote the selection, never send. */
  const handleAskInChat = useCallback((text) => openInky({ prompt: quoteForInky(text) }), [openInky]);

  const handleOpenInNewTab = useCallback(async () => {
    setMenuAnchor(null);
    // Open first (still inside the click, so popup blockers allow it), then flush edits.
    openInNewTab(documentEditorPath(documentId, { projectId }));
    if (isDirty()) await saveNow();
  }, [documentId, projectId, isDirty, saveNow]);

  const handleCopyLink = useCallback(async () => {
    setMenuAnchor(null);
    const url = new URL(documentEditorPath(documentId, { projectId }), window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(url);
      setToast({ severity: 'success', message: 'Link copied' });
    } catch {
      setToast({ severity: 'warning', message: url });
    }
  }, [documentId, projectId]);

  /**
   * Export the current editor content.
   * @param {'word'|'pdf'|'pptx'} type - Format.
   * @returns {Promise<void>}
   */
  const handleExport = useCallback(async (type) => {
    setMenuAnchor(null);
    setExportAnchor(null);
    const html = editorRef.current?.getHTML?.() || liveHtml;
    if (!html || isEmptyHtml(html)) {
      setToast({ severity: 'warning', message: 'There is nothing to export yet.' });
      return;
    }
    const base = fileBaseName(title);
    const projectName = (projectId && appActions.getProjectById(projectId)?.name) || '';
    // The page setup as it is now (also unsaved changes) and the live-field values, so Word and
    // PDF match the Pages view. Slides ignore page setup.
    const pageOptions = { pageSetup: pageSetupRef.current, fieldContext };
    setExporting(type);
    try {
      if (type === 'pptx') {
        const { default: pptxExportService } = await import('../services/pptxExportService');
        // The editor drops the stored slide JSON (an HTML comment). While the deck is unchanged
        // since it was opened, export the stored deck so slide order, grouping and hidden slides
        // match the generated deck exactly.
        const stored = typeof documentData?.content === 'string' ? documentData.content : '';
        const unchanged = loadedEditorHtmlRef.current !== null && html === loadedEditorHtmlRef.current;
        const source = unchanged && /<!--ppt_slides:/.test(stored) ? stored : html;
        await pptxExportService.exportToPptx(source, `${base}.pptx`, projectName, { document: documentData });
      } else {
        const { default: enhancedExportService } = await import('../services/enhancedExportService');
        if (type === 'word') await enhancedExportService.exportToWord(html, `${base}.docx`, projectName, pageOptions);
        else await enhancedExportService.exportToPDF(html, `${base}.pdf`, projectName, pageOptions);
      }
    } catch (error) {
      console.error(`${type} export failed:`, error);
      setToast({ severity: 'error', message: `Export failed: ${error?.message || 'unknown error'}` });
    } finally {
      setExporting(null);
    }
  }, [liveHtml, title, projectId, appActions, fieldContext, documentData]);

  const isDeck = isDeckDocument(documentData);
  const ready = !loading && !loadError;

  const header = (
    <Box
      component="header"
      ref={headerRef}
      sx={{
        position: 'sticky',
        top: 'var(--q-page-sticky-top, 0px)',
        zIndex: 20,
        minHeight: HEADER_HEIGHT,
        display: focusMode ? 'none' : 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: { xs: 0.5, sm: 1 },
        rowGap: 0.5,
        px: { xs: 0.5, sm: 1.5 },
        py: 0.75,
        bgcolor: 'var(--q-surface, #FFFFFF)',
        borderBottom: '1px solid var(--q-border, #E3E0DA)',
      }}
    >
      <EditorNavButton />
      <Tooltip title="Back to documents">
        <IconButton aria-label="Back to documents" onClick={handleBack}>
          <ArrowBack />
        </IconButton>
      </Tooltip>
      <Box sx={{ flex: '1 1 220px', minWidth: 180, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <EntityBreadcrumbs documentId={documentId} projectId={projectId} current={loading && !documentData ? 'Loading…' : title} sx={{ fontSize: 12.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography component="h1" noWrap title={title} sx={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', minWidth: 0 }}>
            {loading && !documentData ? 'Loading…' : title}
          </Typography>
          {ready && (
            <SaveStatusChip status={autosave.status} error={autosave.error} lastSavedAt={autosave.lastSavedAt} halted={autosave.isHalted()} />
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, ml: 'auto', flex: 'none' }}>
        {ready && <DocumentSourcesChip items={sourceItems} onSelect={handleSelectSource} onFix={handleFixSource} />}
        <EditorJobsButton />
        {ready && <InkyReopenButton />}
        {ready && (
          <Button
            variant="contained"
            disableElevation
            aria-haspopup="menu"
            aria-expanded={exportAnchor ? 'true' : undefined}
            onClick={(event) => setExportAnchor(event.currentTarget)}
            disabled={Boolean(exporting)}
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined />}
            endIcon={<ArrowDropDown />}
            aria-label="Export"
            sx={{ borderRadius: 999, flex: 'none', whiteSpace: 'nowrap', minWidth: 0, px: { xs: 1.25, sm: 2 }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export</Box>
          </Button>
        )}
        <Tooltip title="More">
          <IconButton
            aria-label="Document actions"
            aria-haspopup="menu"
            aria-expanded={menuAnchor ? 'true' : undefined}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            disabled={!documentId}
          >
            <MoreVert />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 210 } } }}
      >
        {isDeck ? (
          <MenuItem onClick={() => handleExport('pptx')} disabled={Boolean(exporting)}>
            <ListItemIcon><SlideshowOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>PowerPoint (.pptx)</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleExport('word')} disabled={Boolean(exporting)}>
            <ListItemIcon><DescriptionOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Word (.docx)</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleExport('pdf')} disabled={Boolean(exporting)}>
          <ListItemIcon><PictureAsPdfOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>PDF</ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        {ready && [
          ...[
            { mode: 'pages', label: 'Pages', icon: <ViewAgendaOutlined fontSize="small" /> },
            { mode: 'page', label: 'Page width', icon: <WidthNormal fontSize="small" /> },
            { mode: 'wide', label: 'Full width', icon: <WidthWide fontSize="small" /> },
          ].map(({ mode, label, icon }) => (
            <MenuItem
              key={mode}
              role="menuitemradio"
              aria-checked={editorPageMode === mode ? 'true' : 'false'}
              selected={editorPageMode === mode}
              onClick={() => {
                setMenuAnchor(null);
                setEditorPageMode(mode);
              }}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText>{label}</ListItemText>
              {editorPageMode === mode && <Check fontSize="small" aria-hidden="true" sx={{ ml: 2 }} />}
            </MenuItem>
          )),
          ...(editorPageMode === 'pages' ? [
            <Divider key="d-paper" />,
            // A separate radio set from the views above: its own heading, and names that say
            // what it sets (the heading itself is visual only).
            <ListSubheader key="paper-heading" aria-hidden="true" sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>
              Paper size
            </ListSubheader>,
            ...PAPER_SIZE_IDS.map((id) => (
              <MenuItem
                key={`paper-${id}`}
                role="menuitemradio"
                aria-label={`${PAPER_SIZES[id].label} paper size`}
                aria-checked={paperSize === id ? 'true' : 'false'}
                selected={paperSize === id}
                onClick={() => {
                  setMenuAnchor(null);
                  setPaperSize(writePaperSize(id));
                }}
              >
                <ListItemIcon />
                <ListItemText>{PAPER_SIZES[id].label}</ListItemText>
                {paperSize === id && <Check fontSize="small" aria-hidden="true" sx={{ ml: 2 }} />}
              </MenuItem>
            )),
            <Divider key="d-paper-end" />,
          ] : []),
          ...(!isDeck ? [
            <MenuItem
              key="page-setup"
              onClick={() => {
                setMenuAnchor(null);
                setPageSetupOpen(true);
              }}
            >
              <ListItemIcon><MarginOutlined fontSize="small" /></ListItemIcon>
              <ListItemText>Page setup…</ListItemText>
            </MenuItem>,
          ] : []),
          <MenuItem
            key="focus"
            onClick={() => {
              setMenuAnchor(null);
              setFocusMode(true);
            }}
          >
            <ListItemIcon><CenterFocusStrongOutlined fontSize="small" /></ListItemIcon>
            <ListItemText secondary={formatShortcut('Mod+Shift+F')}>Focus mode</ListItemText>
          </MenuItem>,
          <Divider key="d1" />,
        ]}
        <MenuItem onClick={handleOpenInNewTab}>
          <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon>
          <ListItemText>Open in new tab</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
          <ListItemText>Copy link</ListItemText>
        </MenuItem>
        {ready && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              handleManualSave();
            }}
          >
            <ListItemIcon><SaveOutlined fontSize="small" /></ListItemIcon>
            <ListItemText secondary={formatShortcut('Mod+S')}>Save now</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );

  const editorElement = useMemo(() => (
    <EnhancedRichTextEditor
      ref={editorRef}
      documentKey={`${documentId}:${loadVersion}`}
      initialContent={initialContent}
      onDirty={markDirty}
      onChange={handleChange}
      onSave={handleManualSave}
      onReady={setEditorReady}
      meta={{ documentId, title }}
      placeholder="Start writing your document…"
      pageMode={editorPageMode}
      onPageModeChange={setEditorPageMode}
      paperSize={paperSize}
      onPaperSizeChange={setPaperSize}
      focusMode={focusMode}
      onRequestFocusMode={setFocusMode}
      onAskInChat={handleAskInChat}
      generatedSectionActions={generatedSectionActions}
      enableAdvancedFormatting
      enableImages
    />
  ), [
    documentId,
    loadVersion,
    initialContent,
    markDirty,
    handleChange,
    handleManualSave,
    title,
    editorPageMode,
    setEditorPageMode,
    paperSize,
    focusMode,
    setFocusMode,
    handleAskInChat,
    generatedSectionActions,
  ]);

  const pageSx = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    bgcolor: 'var(--q-canvas, #EEECE7)',
    '--q-editor-sticky-top': focusMode ? 'var(--q-page-sticky-top, 0px)' : `calc(var(--q-page-sticky-top, 0px) + ${headerHeight}px)`,
  };

  if (loading && !documentData) {
    return (
      <Box data-page-height="fill" sx={pageSx}>
        {header}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
          <CircularProgress size={32} />
          <Typography color="text.secondary">Loading document…</Typography>
        </Box>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box data-page-height="fill" sx={pageSx}>
        {header}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
          <Alert severity="error" sx={{ maxWidth: 520 }}>{loadError}</Alert>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={loadDocument}>Retry loading</Button>
            <Button onClick={handleBack}>Back to documents</Button>
          </Box>
        </Box>
      </Box>
    );
  }

  const page = (
    <Box data-page-height="fill" sx={pageSx}>
      {header}

      {autosave.status === 'conflict' && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0 }}
          action={(
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" size="small" onClick={handleReloadFromServer}>Reload</Button>
              <Button color="inherit" size="small" variant="outlined" onClick={handleOverwrite}>Keep mine</Button>
            </Box>
          )}
        >
          This document was changed in another tab or by a teammate. Reload their version (your unsaved edits are discarded) or keep yours and overwrite.
        </Alert>
      )}

      {isDeck && <DeckGenerationNotice doc={documentData} html={liveHtml} sx={{ borderRadius: 0 }} />}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          px: { xs: 1, sm: 1.5 },
          pt: focusMode ? 6 : 1,
          pb: 4,
        }}
      >
        {/* Sources live in the header chip, so the page stays centred with no side column. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>{editorElement}</Box>
      </Box>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.severity === 'success' ? 1800 : 5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert onClose={() => setToast(null)} severity={toast.severity}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );

  // The Page setup dialog (header menu and toolbar More menu) and the page setup for the editor.
  return (
    <PageSetupProvider
      pageSetup={pageSetup}
      onChange={isDeck ? undefined : handlePageSetupChange}
      fieldContext={fieldContext}
      templateId={documentData?.template_id || null}
      target="document"
      paperSize={paperSize}
      open={pageSetupOpen}
      onOpenChange={setPageSetupOpen}
    >
      {page}
    </PageSetupProvider>
  );
};

export default StandaloneEditor;
